import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { articlesService } from '../services/api';
import { Article, ArticleCategory } from '../types';
import { useSeoHead } from '../utils/seoHead';
import ArticleCard from '../components/blog/ArticleCard';
import FeaturedSection from '../components/blog/FeaturedSection';
import BlogPagination from '../components/blog/BlogPagination';
import { ActiveFiltersBar, BlogSearchBar, BlogToolbar, CategoryChips, BlogSort } from '../components/blog/BlogControls';
import { ArticleGridSkeleton, BlogEmptyState, BlogErrorState } from '../components/blog/BlogStates';
import { useBlogSettings } from '../components/blog/blogSettings';

const PAGE_SIZE = 9;
const SITE_URL = 'https://manehaghighi.com';

/** فیلترهای صفحه از query string خوانده می‌شوند تا لینک‌ها قابل اشتراک باشند */
const readSort = (value: string | null): BlogSort => (value === 'popular' ? 'popular' : 'newest');

const Articles: React.FC = () => {
  const settings = useBlogSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get('q') || '').trim();
  const categorySlug = searchParams.get('category');
  const tag = searchParams.get('tag');
  const sort = readSort(searchParams.get('sort'));
  const hasVideo = searchParams.get('video') === '1';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);

  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [featured, setFeatured] = useState<Article[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const requestRef = useRef(0);

  const isFiltering = !!(query || categorySlug || tag || hasVideo);
  /** طبق طرح، بخش منتخب هنگام جست‌وجو/فیلتر پنهان می‌شود */
  const showFeatured = !isFiltering && page === 1 && featured.length > 0;

  useSeoHead(
    {
      title: query ? `جست‌وجوی «${query}» | ${settings.heroTitle}` : `${settings.heroTitle} | ${settings.heroSubtitle}`,
      description: settings.heroSubtitle,
      canonical: `${SITE_URL}/articles`,
      robots: isFiltering || page > 1 ? 'noindex,follow' : undefined,
      meta: [
        { property: 'og:title', content: settings.heroTitle },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: `${SITE_URL}/articles` },
      ],
    },
    [query, isFiltering, page, settings.heroTitle, settings.heroSubtitle],
  );

  // دسته‌ها و مقالات شاخص فقط یک بار
  useEffect(() => {
    articlesService.getCategories().then(setCategories).catch(() => setCategories([]));
    articlesService.getFeatured(3).then(setFeatured).catch(() => setFeatured([]));
  }, []);

  const fetchArticles = useCallback(async () => {
    const requestId = ++requestRef.current;
    setLoading(true);
    setError('');
    try {
      const response = await articlesService.getPublished({
        page,
        limit: PAGE_SIZE,
        search: query || undefined,
        category: categorySlug || undefined,
        tag: tag || undefined,
        sort,
        hasVideo: hasVideo || undefined,
      });
      if (requestId !== requestRef.current) return;
      setArticles(response.data);
      setTotal(response.meta.total);
      setTotalPages(response.meta.totalPages);
    } catch (err: any) {
      if (requestId !== requestRef.current) return;
      setError(err?.response?.data?.message || 'ارتباط با سرور برقرار نشد.');
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [page, query, categorySlug, tag, sort, hasVideo]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles, reloadKey]);

  /** هر تغییر فیلتر/جست‌وجو صفحه را به ۱ برمی‌گرداند */
  const updateParams = useCallback(
    (patch: Record<string, string | null>, resetPage = true) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(patch).forEach(([key, value]) => {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
      });
      if (resetPage) next.delete('page');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const clearAll = () => setSearchParams(new URLSearchParams());

  const activeCategory = useMemo(() => categories.find((c) => c.slug === categorySlug) || null, [categories, categorySlug]);

  const handlePageChange = (next: number) => {
    updateParams({ page: next > 1 ? String(next) : null }, false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 pb-16 pt-6 sm:px-8 sm:pb-20 sm:pt-8">
      {/* هدر سرمقاله‌ای */}
      <header className="flex flex-col gap-1 pb-4 sm:gap-2 sm:border-b sm:border-[#e9e8e5] sm:pb-5 sm:pt-1">
        <h1 className="text-[28px] font-bold leading-[44px] tracking-[-0.7px] text-[#331751] sm:text-[30px] sm:leading-9 sm:tracking-[-0.75px]">
          {settings.heroTitle}
        </h1>
        <p className="text-[14px] leading-[22.75px] text-[#5b5f66] sm:text-[16px] sm:leading-[26px] sm:text-[#52575e]">{settings.heroSubtitle}</p>
      </header>

      {/* جست‌وجو و دسته‌ها */}
      <section className="flex flex-col gap-5 sm:gap-4 sm:pt-6" aria-label="جست‌وجو و دسته‌بندی">
        <BlogSearchBar value={query} onSubmit={(value) => updateParams({ q: value || null })} onClear={() => updateParams({ q: null })} />
        <CategoryChips categories={categories} activeSlug={categorySlug} onSelect={(slug) => updateParams({ category: slug })} />
      </section>

      {showFeatured && <FeaturedSection articles={featured} />}

      <BlogToolbar
        total={total}
        sort={sort}
        onSortChange={(value) => updateParams({ sort: value === 'newest' ? null : value })}
        hasVideo={hasVideo}
        onHasVideoChange={(value) => updateParams({ video: value ? '1' : null })}
        query={query}
      />

      <div className="flex flex-col gap-[14px] pt-4 sm:gap-6 lg:pt-6">
        {isFiltering && !loading && !error && (
          <ActiveFiltersBar
            query={query}
            categoryName={activeCategory?.name}
            tag={tag}
            hasVideo={hasVideo}
            total={total}
            sort={sort}
            onClear={clearAll}
          />
        )}

        {error ? (
          <BlogErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
        ) : loading && articles.length === 0 ? (
          <ArticleGridSkeleton count={PAGE_SIZE} />
        ) : articles.length === 0 ? (
          <BlogEmptyState query={query || (isFiltering ? 'فیلترهای انتخابی' : undefined)} onClear={isFiltering ? clearAll : undefined} />
        ) : (
          <div className={`grid grid-cols-1 items-start gap-[14px] transition-opacity sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 ${loading ? 'opacity-60' : ''}`} aria-busy={loading}>
            {articles.map((article, index) => (
              <ArticleCard key={article.id} article={article} index={index} />
            ))}
          </div>
        )}

        <BlogPagination page={page} totalPages={totalPages} onChange={handlePageChange} />
      </div>
    </div>
  );
};

export default Articles;
